import React, { useMemo } from "react";
import { FormField } from "../../../../shared/components/forms/FormField";
import { Card } from "../../../../shared/components/ui/Card";
import type { UseFormRegister, FieldErrors } from "react-hook-form";
import type { PoolRegulation } from "../../../../api/client/types/catalog.types";
import type { InterventionLevel } from "../../../../api/client/types/catalog.types";
import type { PoolRegulationNewFormData } from "./PoolRegulationNewFormFields.types";

interface PoolRegulationNewFormFieldsProps {
  register: UseFormRegister<PoolRegulationNewFormData>;
  errors: FieldErrors<PoolRegulationNewFormData>;
  poolRegulations: PoolRegulation[];
  interventionLevels: InterventionLevel[];
  showCard?: boolean; // Control whether to wrap content in a Card component
}

/**
 * PoolRegulationNewFormFields
 * Renders only the pool regulation-specific fields.
 * Designed to be used within a parent form that handles common fields.
 * 
 * @param showCard - If true, wraps content in a Card with title. Default: true
 */
export const PoolRegulationNewFormFields: React.FC<PoolRegulationNewFormFieldsProps> = ({
  register,
  errors,
  poolRegulations,
  interventionLevels,
  showCard = true,
}) => {
  const poolRegulationOptions = useMemo(
    () =>
      poolRegulations.map((poolRegulation) => ({
        label: poolRegulation.name,
        value: poolRegulation.id.toString(),
      })),
    [poolRegulations]
  );

  const interventionLevelOptions = useMemo(
    () =>
      interventionLevels.map((interventionLevel) => ({
        label: interventionLevel.name,
        value: interventionLevel.id.toString(),
      })),
    [interventionLevels]
  );

  const content = (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Pool Regulation Type */}
      <FormField
        type="select"
        label="Tipo de Incumplimiento"
        required
        selectProps={{
          ...register("pool_regulation_id"),
          options: [
            { value: "", label: "Seleccione un tipo de incumplimiento" },
            ...poolRegulationOptions,
          ],
          className: "text-sm",
        }}
        error={errors.pool_regulation_id?.message}
      />

      {/* Intervention Level */}
      <FormField
        type="select"
        label="Nivel de Intervención"
        required
        selectProps={{
          ...register("intervention_level_id"),
          options: [
            { value: "", label: "Seleccione un nivel de intervención" },
            ...interventionLevelOptions,
          ],
          className: "text-sm",
        }}
        error={errors.intervention_level_id?.message}
      />
    </div>
  );

  if (!showCard) {
    return content;
  }

  return (
    <Card variant="formSection">
      <h2 className="text-lg font-medium text-gray-800 mb-4">
        Información de Incumplimiento de Reglamento
      </h2>
      {content}
    </Card>
  );
};

